'use client';

import { useDialog } from '@musinsa/mds-ui';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef } from 'react';

export const PENDING_COUPON_DIALOG_QUERY_KEY = 'fromPendingCoupon';

const ShopCouponPendingDialogInner = () => {
  const dialog = useDialog();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isFlagged = searchParams.get(PENDING_COUPON_DIALOG_QUERY_KEY) === 'true';
  const dialogIsOpen = dialog.state.isOpen;

  // 우리가 다이얼로그를 한 번 open 호출했는지 (중복 open 방지)
  const hasOpenedRef = useRef(false);
  // 다이얼로그가 실제로 isOpen=true 상태를 관측한 적이 있는지 (close 판정에 사용)
  // dialog.open() 호출 직후 effect 가 한 번 더 fire 되는 race 에서 dialogIsOpen 이
  // 아직 false 인 상태를 "닫힘" 으로 오인하여 query 를 조기 제거하는 버그를 방지.
  const hasBeenOpenRef = useRef(false);

  useEffect(() => {
    if (!isFlagged) {
      hasOpenedRef.current = false;
      hasBeenOpenRef.current = false;
      return;
    }

    // 최초 1회 다이얼로그 오픈
    if (!hasOpenedRef.current) {
      hasOpenedRef.current = true;
      dialog.open({
        title: '진행중인 쿠폰 캠페인이 아닙니다. 일정을 다시 확인해 주세요.',
        confirmButtonText: '닫기',
      });
      return;
    }

    // isOpen=true 가 한 번이라도 관측되어야 close 판정 시작
    if (dialogIsOpen) {
      hasBeenOpenRef.current = true;
      return;
    }

    // open 됐던 적이 있는데 지금은 닫힘 → 닫기 버튼 / dim / esc 등으로 닫힘 확정.
    if (hasBeenOpenRef.current) {
      hasOpenedRef.current = false;
      hasBeenOpenRef.current = false;
      const next = new URLSearchParams(searchParams.toString());
      next.delete(PENDING_COUPON_DIALOG_QUERY_KEY);
      const query = next.toString();
      router.replace(query ? `${pathname}?${query}` : pathname);
    }
  }, [isFlagged, dialogIsOpen, dialog, router, pathname, searchParams]);

  return null;
};

/**
 * 방문자가 진행 예정 쿠폰 페이지로 직접 진입하면 큐레이션 샵으로 리다이렉트하면서
 * `?pendingCoupon=1` 플래그를 붙여준다. 이 컴포넌트는 해당 플래그를 감지해
 * 안내 다이얼로그를 노출하고, 다이얼로그가 닫히는 즉시 router.replace 로 플래그를 제거한다.
 */
export const ShopCouponPendingDialog = () => (
  <Suspense fallback={null}>
    <ShopCouponPendingDialogInner />
  </Suspense>
);
